import React, { useEffect, useRef } from 'react';
type Props = {
  src: string;
  className?: string;
  /** Grid pitch in CSS px — matches the hero's coarse dot grid. */
  cell?: number;
  /** Dot size in CSS px. */
  dot?: number;
  color?: string;
  /** Hero-style "trail": dots right under the cursor scatter and flare briefly. */
  interactive?: boolean;
};

// 4×4 ordered (Bayer) threshold map — same 1-bit look as the hero field
const BAYER = [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5].map(v => (v + 0.5) / 16);

// Trail tuning — matches the hero's "trail" hover (small, direct, short-lived)
const RADIUS = 20; // px around each touch
const PUSH = 1.5; // max outward drift in px — a gentle nudge, never a gap
const LIFE = 700; // ms a touch eases in and out
const FLARE = 0.1; // extra opacity at the centre

type Touch = {
  x: number;
  y: number;
  t: number;
};

/**
 * Renders an image as a low-fidelity 1-bit dot matrix: uniform square dots on a
 * coarse grid, tone carried only by dot density — matching the hero visual.
 */
export function DitherImage({
  src,
  className = '',
  cell = 4,
  dot = 2,
  color = '176,186,214',
  interactive = false
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let img: HTMLImageElement | null = null;
    let cancelled = false;
    let dots: {
      x: number;
      y: number;
      a: number;
    }[] = [];
    let w = 0;
    let h = 0;
    let dpr = 1;
    const touches: Touch[] = [];
    let raf = 0;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const canHover = window.matchMedia('(hover: hover)').matches;
    const paint = (now: number) => {
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);
      // Drop expired touches
      while (touches.length && now - touches[0].t > LIFE) touches.shift();
      for (const d of dots) {
        // Take only the strongest touch per dot (never sum) so fast sweeps can't stack into a hole
        let best = 0;
        let ux = 0;
        let uy = 0;
        for (const tc of touches) {
          const dx = d.x - tc.x;
          const dy = d.y - tc.y;
          const dist = Math.hypot(dx, dy);
          if (dist >= RADIUS) continue;
          const age = (now - tc.t) / LIFE;
          // Smooth rise-and-fall over the touch's life (no hard start)
          const life = Math.sin(Math.PI * Math.min(1, Math.max(0, age)));
          const fall = 1 - dist / RADIUS;
          const k = fall * fall * (3 - 2 * fall) * life;
          if (k > best) {
            best = k;
            ux = dist > 0.01 ? dx / dist : 0;
            uy = dist > 0.01 ? dy / dist : 0;
          }
        }
        ctx.fillStyle = `rgba(${color},${Math.min(0.9, d.a + FLARE * best)})`;
        ctx.fillRect(d.x + ux * PUSH * best, d.y + uy * PUSH * best, dot, dot);
      }
    };
    const loop = (now: number) => {
      paint(now);
      raf = touches.length ? requestAnimationFrame(loop) : 0;
    };
    const build = () => {
      if (!img || cancelled) return;
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      if (!w || !h) return;
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);

      // Sample the image (object-fit: cover) at one pixel per grid cell
      const cols = Math.ceil(w / cell);
      const rows = Math.ceil(h / cell);
      const off = document.createElement('canvas');
      off.width = cols;
      off.height = rows;
      const octx = off.getContext('2d', {
        willReadFrequently: true
      });
      if (!octx) return;
      const scale = Math.max(cols / img.naturalWidth, rows / img.naturalHeight);
      const dw = img.naturalWidth * scale;
      const dh = img.naturalHeight * scale;
      octx.drawImage(img, (cols - dw) / 2, (rows - dh) / 2, dw, dh);
      let data: Uint8ClampedArray;
      try {
        data = octx.getImageData(0, 0, cols, rows).data;
      } catch {
        return; // cross-origin blocked — leave empty rather than break
      }
      dots = [];
      for (let y = 0; y < rows; y++) {
        for (let x = 0; x < cols; x++) {
          const i = (y * cols + x) * 4;
          let l = (data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114) / 255;
          // Crush the dark ground, lift the subject — keeps it graphic, not photographic
          l = Math.min(1, Math.max(0, (l - 0.12) * 1.5));
          if (l <= BAYER[(x & 3) + ((y & 3) << 2)]) continue;
          // Two quiet tones only: brighter dots in the lightest areas
          dots.push({
            x: x * cell,
            y: y * cell,
            a: l > 0.75 ? 0.7 : 0.42
          });
        }
      }
      paint(performance.now());
    };
    const onMove = (e: PointerEvent) => {
      const r = canvas.getBoundingClientRect();
      const x = e.clientX - r.left;
      const y = e.clientY - r.top;
      if (x < -RADIUS || y < -RADIUS || x > r.width + RADIUS || y > r.height + RADIUS) return;
      // Convert to the canvas's own (untransformed) space
      const sx = w / r.width;
      const sy = h / r.height;
      const now = performance.now();
      const last = touches[touches.length - 1];
      const px = x * sx;
      const py = y * sy;
      // Fill gaps on fast moves so the trace stays continuous
      if (last) {
        const gap = Math.hypot(px - last.x, py - last.y);
        const steps = Math.min(8, Math.floor(gap / (RADIUS * 0.6)));
        for (let s = 1; s <= steps; s++) {
          const f = s / (steps + 1);
          touches.push({
            x: last.x + (px - last.x) * f,
            y: last.y + (py - last.y) * f,
            t: now
          });
        }
      }
      touches.push({
        x: px,
        y: py,
        t: now
      });
      if (touches.length > 60) touches.splice(0, touches.length - 60);
      if (!raf) raf = requestAnimationFrame(loop);
    };
    const el = new Image();
    el.crossOrigin = 'anonymous';
    el.onload = () => {
      img = el;
      build();
    };
    el.src = src;
    const ro = new ResizeObserver(() => build());
    ro.observe(canvas);
    const listen = interactive && canHover && !reduce;
    if (listen) window.addEventListener('pointermove', onMove, {
      passive: true
    });
    return () => {
      cancelled = true;
      ro.disconnect();
      if (listen) window.removeEventListener('pointermove', onMove);
      cancelAnimationFrame(raf);
    };
  }, [src, cell, dot, color, interactive]);
  return <canvas ref={canvasRef} aria-hidden="true" className={`pointer-events-none absolute select-none ${className}`} data-id="element-86" data-mp="2tz8qaxpram6z" />;
}